import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';

import { getUserInfo } from '../../../../action/ClientActions';
import {
	getMfaStatus,
	setMfaMethod,
	verifyMfaCode,
	resendMfaCode,
	disableMfa,
	setupTotpMfa,
} from '../../../../action/AccountActions';

import CustomButton from '../../../CustomButton';
import { useAuth } from '../../../../../../context/AuthContext';
import { arrowRight } from '../../../helpers/iconHelpers';

const MFAPage = (props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { login } = useAuth();
	const { t } = useTranslation('translation', { keyPrefix: 'loginForms.mfaPage' });

	const mode = location.pathname.replace('/mfa-', '') || 'settings';
	const verifyMethod = props.verifyMethod || location.state?.verifyMethod || null;
	const token = props.token || location.state?.token || null;

	const [screen, setScreen] = useState(() => {
		if (mode === 'verify' && verifyMethod) return 'verify';
		return 'select';
	});

	const [mfaEnabled, setMfaEnabled] = useState(false);
	const [selectedMethod, setSelectedMethod] = useState(null);
	const [verifyCode, setVerifyCode] = useState('');
	const [codeError, setCodeError] = useState('');
	const [totpSecret, setTotpSecret] = useState('');
	const [totpQr, setTotpQr] = useState('');
	const [resendCooldown, setResendCooldown] = useState(0);

	const toggleLoader = useCallback(
		(show) => dispatch({ type: 'TOGGLE_LOADER', payload: show }),
		[dispatch],
	);

	useEffect(() => {
		if (mode === 'settings') {
			toggleLoader(true);
			getMfaStatus()
				.then((res) => {
					const data = res?.data?.response;
					setMfaEnabled(!!data?.mfa_enabled);
					setSelectedMethod(data?.mfa_method || null);
				})
				.catch(() => { })
				.finally(() => toggleLoader(false));
		}
	}, [mode]);

	useEffect(() => {
		if (resendCooldown <= 0) return;
		const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);

		return () => clearTimeout(timer);
	}, [resendCooldown]);

	const navigateHome = () => {
		getUserInfo()
			.then((resUserInfo) => {
				toggleLoader(false);
				if (resUserInfo.data.response.user.eula_was_viewed) {
					navigate('/', { replace: true });
				} else {
					navigate('/eula-agreement', { replace: true, state: { isLoginMode: true } });
				}
			})
			.catch(() => {
				toggleLoader(false);
				navigate('/', { replace: true });
			});
	}

	const handleSelectMethod = (method) => {
		// toggleLoader(true);
		// if (method === 'totp') {
		// 	setupTotpMfa()
		// 		.then((res) => {
		// 			const data = res?.data?.response;
		// 			setTotpSecret(data?.secret_key || '');
		// 			setTotpQr(data?.qr_code_url || '');
		// 			setSelectedMethod('totp');
		// 			setScreen('totp-setup');
		// 		})
		// 		.catch(() => {
		// 			toastr.error(t('errorSetupTotp'));
		// 		})
		// 		.finally(() => {
		// 			toggleLoader(false);
		// 		});
		// 	return;
		// }

		// setMfaMethod(method)
		// 	.then(() => {
		setSelectedMethod(method);
		setMfaEnabled(true);
		if (method === 'totp') {
			setScreen('totp-setup');
			setTotpQr('https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=otpauth://totp/Rosco:demo%40roscovision.com?secret=JBSWY3DPEHPK3PXP&issuer=Rosco');
		} else {
			setScreen('verify');
		}
		// })
		// .catch(() => {
		// 	toastr.error(t('errorSetMethod'));
		// })
		// .finally(() => {
		// 	toggleLoader(false);
		// });
	};

	const handleToggleMfa = () => {
		if (mfaEnabled) {
			toggleLoader(true);
			disableMfa()
				.then(() => {
					setMfaEnabled(false);
					setSelectedMethod(null);
					toastr.success(t('mfaDisabled'));
				})
				.catch(() => {
					toastr.error(t('errorDisableMfa'));
				})
				.finally(() => {
					toggleLoader(false);
				});
		} else {
			setMfaEnabled(false);
		}
	};

	const handleVerifyTotp = (e) => {
		e.preventDefault();
		if (!verifyCode.trim()) {
			setCodeError(t('errEnterCode'));
			return;
		}
		toggleLoader(true);
		verifyMfaCode({ code: verifyCode, token })
			.then((res) => {
				const data = res?.data?.response;
				if (data?.error) {
					setCodeError(data.error);
					return;
				}

				setMfaEnabled(true);
				toastr.success(t('totpEnabled'));

				if (data?.auth_token) {
					return login({ auth_token: data.auth_token, auth_refresh_token: data.auth_refresh_token })
						.then(() => { navigateHome(); });
				}

				if (mode === 'verify') {
					navigateHome();
					return;
				}

				setScreen('success');
			})
			.catch(() => {
				setCodeError(t('errInvalidCode'));
			})
			.finally(() => {
				toggleLoader(false);
			});
	};

	const handleVerifyCode = (e) => {
		e.preventDefault();
		if (!verifyCode.trim()) {
			setCodeError(t('errEnterCode'));
			return;
		}
		toggleLoader(true);
		verifyMfaCode({ code: verifyCode, token })
			.then((res) => {
				const data = res?.data?.response;
				if (data?.error) {
					setCodeError(data.error);
					return;
				}

				if (data?.auth_token) {
					return login({ auth_token: data.auth_token, auth_refresh_token: data.auth_refresh_token })
						.then(() => { navigateHome(); });
				}

				if (mode === 'verify') {
					navigateHome();
					return;
				}

				setMfaEnabled(true);
				toastr.success(t('mfaVerified'));
				setScreen('success');
			})
			.catch(() => {
				setCodeError(t('errInvalidCode'));
			})
			.finally(() => {
				toggleLoader(false);
			});
	};

	const handleResend = () => {
		if (resendCooldown > 0) return;
		resendMfaCode({ token })
			.then(() => {
				setResendCooldown(30);
				toastr.success(t('codeSent'));
			})
			.catch(() => {
				toastr.error(t('errorResend'));
			});
	};

	const methodList = [
		{
			key: 'email',
			icon: (
				<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#DBEAFE" />
					<path d="M12.5 16.6667L19.075 21.05C19.3489 21.2327 19.6708 21.3302 20 21.3302C20.3292 21.3302 20.6511 21.2327 20.925 21.05L27.5 16.6667M14.1667 25.8333H25.8333C26.2754 25.8333 26.6993 25.6577 27.0118 25.3452C27.3244 25.0326 27.5 24.6087 27.5 24.1667V15.8333C27.5 15.3913 27.3244 14.9674 27.0118 14.6548C26.6993 14.3423 26.2754 14.1667 25.8333 14.1667H14.1667C13.7246 14.1667 13.3007 14.3423 12.9882 14.6548C12.6756 14.9674 12.5 15.3913 12.5 15.8333V24.1667C12.5 24.6087 12.6756 25.0326 12.9882 25.3452C13.3007 25.6577 13.7246 25.8333 14.1667 25.8333Z" stroke="#155DFC" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			),
			label: t('methodEmailLabel'),
			desc: t('methodEmailDesc'),
		},
		{
			key: 'sms',
			icon: (
				<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#DBEAFE" />
					<path d="M20 25H20.0083M16.6667 27.5H23.3333C23.7754 27.5 24.1993 27.3244 24.5118 27.0118C24.8244 26.6993 25 26.2754 25 25.8333V14.1667C25 13.7246 24.8244 13.3007 24.5118 12.9882C24.1993 12.6756 23.7754 12.5 23.3333 12.5H16.6667C16.2246 12.5 15.8007 12.6756 15.4882 12.9882C15.1756 13.3007 15 13.7246 15 14.1667V25.8333C15 26.2754 15.1756 26.6993 15.4882 27.0118C15.8007 27.3244 16.2246 27.5 16.6667 27.5Z" stroke="#155DFC" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			),
			label: t('methodSmsLabel'),
			desc: t('methodSmsDesc'),
		},
		{
			key: 'totp',
			icon: (
				<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#DBEAFE" />
					<path d="M20 22.5V24.1667M15 27.5H25C25.4421 27.5 25.866 27.3244 26.1786 27.0118C26.4911 26.6993 26.6667 26.2754 26.6667 25.8333V20.8333C26.6667 20.3913 26.4911 19.9674 26.1786 19.6548C25.866 19.3423 25.4421 19.1667 25 19.1667H15C14.558 19.1667 14.1341 19.3423 13.8215 19.6548C13.509 19.9674 13.3334 20.3913 13.3334 20.8333V25.8333C13.3334 26.2754 13.509 26.6993 13.8215 27.0118C14.1341 27.3244 14.558 27.5 15 27.5ZM23.3334 19.1667V15.8333C23.3334 14.9493 22.9822 14.1014 22.3571 13.4763C21.7319 12.8512 20.8841 12.5 20 12.5C19.116 12.5 18.2681 12.8512 17.643 13.4763C17.0179 14.1014 16.6667 14.9493 16.6667 15.8333V19.1667H23.3334Z" stroke="#155DFC" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			),
			label: t('methodTotpLabel'),
			desc: t('methodTotpDesc'),
		},
	];

	const methodKey = verifyMethod || selectedMethod;
	const verifyHeader = methodKey === 'sms'
		? t('headerSmsVerify')
		: methodKey === 'totp'
			? t('headerTotpVerify')
			: t('headerEmailVerify');
	const verifySubheader = methodKey === 'sms'
		? t('subheaderSmsVerify')
		: methodKey === 'totp'
			? t('subheaderTotpVerify')
			: t('subheaderEmailVerify');
	const isTotpSetupScreen = screen === 'totp-setup';
	const isVerifyScreen = screen === 'verify';
	const isCodeEntryScreen = isTotpSetupScreen || isVerifyScreen;
	const isCodeComplete = verifyCode.trim().length === 6;
	const isSuccessScreen = screen === 'success';
	const outerClassName = `mfa-page-layout${(isVerifyScreen || isSuccessScreen) ? ' mfa-code-entry-screen' : ''}${isTotpSetupScreen ? ' mfa-totp-entry-screen' : ''}`;
	const panelClassName = `mfa-panel${isSuccessScreen ? ' mfa-success-panel' : ''}${isTotpSetupScreen ? ' mfa-totp-setup-layout' : ''}`;

	return (
		<div className={outerClassName}>
			<div className={panelClassName}>
				{!isSuccessScreen && (
					<div className="mfa-brand">
						<span className="mfa-brand-icon" />
						<span className="mfa-brand-name">rosco</span>
					</div>
				)}

				{screen === 'select' && (
					<>
						<h3 className="login-mfa-header">{t('headerChooseMethod')}</h3>
						<p className="login-mfa-subheader">{t('subheaderChooseMethod')}</p>
						<div className="form-wrapper mfa-method-list">
							{methodList.map(({ key, icon, label, desc }) => (
								<button
									key={key}
									type="button"
									className={`mfa-method-option${selectedMethod === key ? ' active' : ''}`}
									onClick={() => handleSelectMethod(key)}
								>
									<span className="mfa-method-icon">{icon}</span>
									<span className="mfa-method-text">
										<strong>{label}</strong>
										<span>{desc}</span>
									</span>
								</button>
							))}

							<div className="mfa-toggle-link">
								<button
									type="button"
									className="btn-link mfa-disable-link"
									onClick={handleToggleMfa}
								>
									{mfaEnabled ? t('linkDisableMfa') : t('linkEnableMfa')}
								</button>
							</div>
						</div>
					</>
				)}

				{isCodeEntryScreen && (
					<>
						<h3 className="login-mfa-header">{isTotpSetupScreen ? t('headerSetupTotp') : verifyHeader}</h3>
						<p className="login-mfa-subheader">{isTotpSetupScreen ? t('subheaderSetupTotp') : verifySubheader}</p>
						<div className="form-wrapper">
							{isTotpSetupScreen && (
								<div className="mfa-qr-wrapper">
									{totpQr && <img src={totpQr} alt="QR Code" className="mfa-qr-code" />}
								</div>
							)}

							<form onSubmit={isTotpSetupScreen ? handleVerifyTotp : handleVerifyCode}>
								<div className="mfa-form">
									{isTotpSetupScreen && <p className="login-mfa-label">{t('labelManualKey')}</p>}
									{isTotpSetupScreen && (
										<input
											type="text"
											className="mfa-input"
											value={totpSecret}
											onChange={(e) => { setTotpSecret(e.target.value) }}
										/>
									)}
								</div>
								<div className="mfa-form">
									<label className="login-mfa-label">{t('labelVerificationCode')}</label>
									<input
										type="text"
										name={isTotpSetupScreen ? 'totp_code' : 'verify_code'}
										className={`mfa-input${codeError ? ' error' : ''}`}
										placeholder={t('placeholderCode')}
										value={verifyCode}
										onChange={(e) => { setVerifyCode(e.target.value); setCodeError(''); }}
										maxLength={6}
										autoFocus={isCodeEntryScreen}
									/>
									{codeError && <span className="help-block error-label">{codeError}</span>}
								</div>
								<div className="mfa-form submit-btn">
									<CustomButton variant="primary" type="submit" style={{ padding: '8px 0px' }} disabled={!isCodeComplete}>
										{isTotpSetupScreen ? t('btnVerifyEnable') : t('btnVerify')}
									</CustomButton>
								</div>
								{isTotpSetupScreen && (
									<div className="mfa-resend-link">
										<CustomButton
											variant="link"
											type="button"
											onClick={() => setScreen('select')}
										>
											{t('linkSkipForNow')}
										</CustomButton>
									</div>
								)}
								{isVerifyScreen && (
									<div className="mfa-resend-link">
										<CustomButton
											variant="link"
											type="button"
											onClick={handleResend}
											disabled={resendCooldown > 0}
										>
											{resendCooldown > 0 ? `${t('linkResend')} (${resendCooldown}s)` : t('linkResend')}
										</CustomButton>
									</div>
								)}
							</form>
						</div>
					</>
				)}

				{isSuccessScreen && (
					<>
						<div className="mfa-success-icon" aria-hidden="true">
							<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="32" cy="32" r="32" fill="#DCFCE7" />
								<path d="M20 34L28 42L44 26" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
						</div>
						<h3 className="login-mfa-header">{t('headerSuccess')}</h3>
						<p className="login-mfa-subheader">{t('subheaderSuccess')}</p>
						<div className="mfa-success-actions">
							<CustomButton variant="link" type="button" onClick={() => setScreen('select')}>
								{t('btnSuccessContinue')}
							</CustomButton>
						</div>
					</>
				)}
			</div>

			{screen === 'select' && (
				<div className="mfa-side-cards">
					<div className="mfa-side-card mfa-side-card-why">
						<h3 className="mfa-side-card-header">{t('mfaWhyHeader')}</h3>
						<div className="mfa-side-card-subheader">{t('mfaWhySubheader')}</div>
						<a href={t('mfaLearnMoreHref')} className="sales-link" target="_blank" rel="noreferrer">
							{t('mfaLearnMoreLink')}
							{arrowRight}
						</a>
					</div>
					<div className="mfa-side-card mfa-side-card-access">
						<h3 className="mfa-side-card-header">{t('loginHeader')}</h3>
						<div className="mfa-side-card-subheader mt-20">{t('loginSubheader')}</div>
						<a
							href="mailto:sales@roscovision.com"
							className="sales-link"
						>
							{t('linkContactRosco')}
							{arrowRight}
						</a>
						<a href={t('mfaMethodsHref')} className="sales-link" target="_blank" rel="noreferrer">
							{t('mfaMethodsLink')}
							{arrowRight}
						</a>
					</div>
				</div>
			)}
		</div>
	);
};

export default MFAPage;
